/* Copyright (c) 2020-present CloudIO Inc */

import { IoDatasource, Query } from '@cloudio-saas/datasource-types';
import {
  useStateXAtom,
  useStateXAtomValue,
  useStateXGetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { getIoDatasourceStoreProps } from 'store/utils/utils';
import { AppBar, Toolbar, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DataSourceAccess from './DataSourceAccess';
import DataSourceAccessHeader from './DataSourceAccessHeader';
import React, { memo, Suspense, useLayoutEffect } from 'react';
import Loading from 'root/SimpleLoading';
import DataSourceScriptHistory from './DataSourceScriptHistory';
import DataSourceScriptHistoryHeader from './DataSourceScriptHistoryHeader';
import {
  sidebarActiveDatasourceIdAtom,
  sidebarDSViewAtom,
} from 'layout/Sidebar/atoms';
import useDataStore from 'store/useDataStore';
import DataSourceWizard from './Wizard/DataSourceWizard';
import DataSourceWizardNext from './Wizard/DataSourceWizardNext';
import { WithSizeContext } from 'hooks/useComponentSize';
import AttributeEdit from './AttributeEdit';
import AttributesHeader from './AttributesHeader';
import AttributesList from './AttributesList';
import DataSourceEdit from './DataSourceEdit';
import DataSourceHeader from './DataSourceHeader';
import DataSourceList from './DataSourceList';
import { usePlatform } from 'platform/PlatformHooks';
import { isViewOnlyDeveloper } from 'root/useUser';

export const dsFilter: (appUid: string) => Query<IoDatasource> = (appUid) => ({
  filter: [{ appUid: { is: appUid } }],
  limit: 5000,
  offset: 0,
  projection: {
    appUid: 1,
    datasourceType: 1,
    datasourceUid: 1,
    hasAfterScript: 1,
    hasBeforeScript: 1,
    hasQueryScript: 1,
    lastUpdateDate: 1,
    lastUpdatedBy: 1,
    orgUid: 1,
    preventResync: 1,
    typeValue: 1,
    extTypeValue: 1,
    objectConnectionCode: 1,
  },
  sort: { lastUpdateDate: -1 },
});

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'absolute',
    left: 0,
    right: 0,
    top: 65,
    bottom: 0,
  },
  header: {
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
  title: {
    flex: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  toolbar: {
    padding: 0,
  },
}));

export function InitIoDatasource({ appUid }: { appUid: string }) {
  const store = useDataStore<IoDatasource>(getIoDatasourceStoreProps(appUid));
  store.read({ props: dsFilter(appUid) });
  const get = useStateXGetter();
  const set = useStateXSetter();
  const dsid = useStateXAtomValue(sidebarActiveDatasourceIdAtom);
  useLayoutEffect(() => {
    // check and set active id as the current record id
    if (dsid) {
      const record = store.getCurrentRecord();
      if (!record || dsid !== record.datasourceUid) {
        const records = store.records();
        const ro = store.recordsOrder();
        const id = ro.find((_id) => records[_id].datasourceUid === dsid);
        if (id) {
          store.setCurrentRecordId(id);
        } else {
          // active id is not part of the store, hence clear it
          set(sidebarActiveDatasourceIdAtom, record?.datasourceUid ?? '');
        }
      }
    }
  }, [dsid, get, set, store]);
  return null;
}

function DataSourcePanel({
  appUid,
  roleUid,
  width,
}: {
  appUid: string;
  roleUid: string;
  width: number;
}): React.ReactElement {
  const store = useDataStore<IoDatasource>(getIoDatasourceStoreProps(appUid));

  const classes = useStyles();
  const [view, setView] = useStateXAtom(sidebarDSViewAtom);

  const platform = usePlatform();
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  let content;
  switch (view) {
    case 'list':
      content = (
        <Suspense fallback={<Loading name="Sidebar Panel" />}>
          <DataSourceList
            key="list"
            setView={setView}
            store={store}
            appUid={appUid}
            isViewOnlyUser={isViewOnlyUser}
          />
        </Suspense>
      );
      break;
    case 'edit':
      content = (
        <DataSourceEdit
          key="edit"
          setView={setView}
          appUid={appUid}
          width={width}
        />
      );
      break;
    case 'wizard':
      content = (
        <DataSourceWizard
          key="wizard"
          setView={setView}
          appUid={appUid}
          roleUid={roleUid}
          width={width}
        />
      );
      break;
    case 'duplicate':
      content = (
        <DataSourceEdit
          key="duplicate"
          setView={setView}
          appUid={appUid}
          width={width}
        />
      );
      break;
    case 'access':
      content = (
        <DataSourceAccess
          appUid={appUid}
          key="access"
          isViewOnlyUser={isViewOnlyUser}
        />
      );
      break;
    case 'editChild':
      content = <AttributeEdit appUid={appUid} key="editChild" />;
      break;
    case 'listChildren':
      content = (
        <AttributesList appUid={appUid} key="listChildren" setView={setView} />
      );
      break;
    case 'history':
      content = <DataSourceScriptHistory appUid={appUid} />;
      break;
    case 'next':
      content = <DataSourceWizardNext appUid={appUid} setView={setView} />;
      break;
    default:
  }

  let toolBarContent;
  switch (view) {
    case 'edit':
    case 'list':
    case 'next':
    case 'wizard':
      toolBarContent = (
        <DataSourceHeader
          appUid={appUid}
          key="list"
          store={store}
          view={view}
          setView={setView}
        />
      );
      break;
    case 'access':
      toolBarContent = (
        <DataSourceAccessHeader
          appUid={appUid}
          key="access"
          setView={setView}
        />
      );
      break;
    case 'history':
      toolBarContent = (
        <DataSourceScriptHistoryHeader appUid={appUid} setView={setView} />
      );
      break;
    default:
      toolBarContent = (
        <AttributesHeader
          appUid={appUid}
          key="attr"
          view={view}
          setView={setView}
        />
      );
  }

  return (
    <>
      <InitIoDatasource appUid={appUid} />
      <AppBar
        className={classes.header}
        position="relative"
        color="transparent"
      >
        <Toolbar className={classes.toolbar}>{toolBarContent}</Toolbar>
      </AppBar>
      <div className={classes.body}>
        <WithSizeContext>{content}</WithSizeContext>
      </div>
    </>
  );
}

export default memo(DataSourcePanel);
